'use client';

import { Bell, CreditCard, LifeBuoy, LogOut, Shield, UserRound } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import * as React from 'react';

import { POPOVER_ROW_HOVER_HANDLERS } from '@/components/layouts/popover-row-hover';
import { ContactUsModal } from '@/components/support/contact-us-modal';
import { useHasChanged } from '@/hooks/use-has-changed';
import { signOut } from '@/lib/auth/actions';
import { getAvatarColorHex } from '@/lib/avatar-color';
import { logger } from '@/lib/logger';
import { usePostHog } from '@/lib/posthog';
import { clearServiceWorkerCaches } from '@/lib/pwa';
import { getInitials } from '@/lib/user-utils';
import { cn } from '@/lib/utils';

interface AccountMenuProps {
  userEmail: string;
  displayName: string;
  /**
   * Whether the current user is a Loristry admin. Server-resolved (via the
   * `role` column on `profiles`) and passed down — never inferred client-side —
   * so the Admin entry point only renders for genuine admins. The `/admin`
   * route is independently guarded by `requireAdmin`, so this flag controls
   * discoverability, not authorization.
   */
  isAdmin: boolean;
}

const ROW_CLASSES = cn(
  'flex items-center gap-3 px-3 py-2.5 w-full',
  'text-sm font-medium transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
);

export function AccountMenu({ userEmail, displayName, isAdmin }: AccountMenuProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { posthog } = usePostHog();
  const [isOpen, setIsOpen] = React.useState(false);
  const [contactOpen, setContactOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const isSigningOutRef = React.useRef(false);

  const initials = getInitials(displayName, userEmail);
  const avatarColor = getAvatarColorHex(displayName, userEmail);

  // Click outside → close
  React.useEffect(() => {
    if (!isOpen) return;
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isOpen]);

  // Escape → close
  React.useEffect(() => {
    if (!isOpen) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen]);

  // Route change → close. Covers keyboard Enter on links and programmatic
  // navigation, which the per-Link onClick handlers miss. Closed in the new
  // route's first render.
  if (useHasChanged(pathname)) {
    setIsOpen(false);
  }

  const handleSignOut = async () => {
    // Guard against double-click races. A ref (not state) is required because
    // synchronous double-clicks fire before a setState would flush.
    if (isSigningOutRef.current) return;
    isSigningOutRef.current = true;

    setIsOpen(false);
    // Clear service worker caches before signing out so no user data is cached.
    try {
      const result = await clearServiceWorkerCaches();
      if (result.failed > 0) {
        logger.warn('Some caches failed to clear on sign-out, proceeding anyway', undefined, {
          cacheResult: result,
        });
      }
    } catch (error) {
      logger.error('Failed to clear service worker caches on sign-out', {
        error,
      });
    }
    try {
      const { redirectPath } = await signOut();
      // Reset PostHog super properties and distinct ID after sign-out succeeds, so
      // billing tier data doesn't leak to events fired by a subsequent user. Wrapped
      // because reset() can throw in storage-restricted contexts (Safari ITP, quota
      // exceeded) — sign-out has already succeeded, so a reset failure is non-fatal.
      try {
        posthog?.reset();
      } catch (error) {
        logger.warn('PostHog reset failed during sign-out', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
      router.push(redirectPath);
    } catch (error) {
      logger.error('[Auth] Sign out failed', {
        error,
      });
      // Reset the guard so the user can retry without a full page reload if
      // the fallback redirect also fails for some reason.
      isSigningOutRef.current = false;
      router.push('/signed-out');
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center',
          'text-white text-[10px] font-semibold',
          'transition-opacity hover:opacity-90',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
        )}
        style={{ backgroundColor: avatarColor }}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="Account menu"
      >
        {initials}
      </button>

      {/* Popover — not role="menu" because we don't implement arrow-key navigation.
          aria-haspopup="true" advertises a generic popover without promising
          menu semantics. */}
      {isOpen && (
        <div
          data-testid="account-menu-popover"
          className="absolute top-full -right-1 mt-2 rounded-lg shadow-lg border min-w-[280px] overflow-hidden"
          style={{
            backgroundColor: 'var(--neutral-0)',
            borderColor: 'var(--border-default)',
            zIndex: 50,
          }}
        >
          {/* Profile mini-header */}
          <div
            className="flex items-center gap-3 px-3 py-3"
            style={{ borderBottom: '1px solid var(--border-default)' }}
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
              style={{ backgroundColor: avatarColor }}
              aria-hidden="true"
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              {/* Use displayName, not preferredName: the adjacent avatar derives
                  its initials from displayName, and the label must share that
                  basis or users see orphan letters in the initials. */}
              <p
                className="text-sm font-semibold truncate"
                style={{ color: 'var(--text-primary)' }}
              >
                {displayName}
              </p>
              {userEmail && (
                <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                  {userEmail}
                </p>
              )}
            </div>
          </div>

          {/* Edit profile */}
          <Link
            href="/settings/profile"
            onClick={() => setIsOpen(false)}
            className={ROW_CLASSES}
            style={{ color: 'var(--text-secondary)' }}
            {...POPOVER_ROW_HOVER_HANDLERS}
          >
            <UserRound
              className="w-4 h-4 flex-shrink-0"
              style={{ color: 'var(--text-secondary)' }}
            />
            <span>Profile</span>
          </Link>

          {/* Notifications */}
          <Link
            href="/settings/notifications"
            onClick={() => setIsOpen(false)}
            className={ROW_CLASSES}
            style={{ color: 'var(--text-secondary)' }}
            {...POPOVER_ROW_HOVER_HANDLERS}
          >
            <Bell className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-secondary)' }} />
            <span>Notifications</span>
          </Link>

          {/* Billing */}
          <Link
            href="/settings/billing"
            onClick={() => setIsOpen(false)}
            className={ROW_CLASSES}
            style={{ color: 'var(--text-secondary)' }}
            {...POPOVER_ROW_HOVER_HANDLERS}
          >
            <CreditCard
              className="w-4 h-4 flex-shrink-0"
              style={{ color: 'var(--text-secondary)' }}
            />
            <span>Billing</span>
          </Link>

          {/* Contact us */}
          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              setContactOpen(true);
            }}
            className={ROW_CLASSES}
            style={{ color: 'var(--text-secondary)' }}
            {...POPOVER_ROW_HOVER_HANDLERS}
          >
            <LifeBuoy
              className="w-4 h-4 flex-shrink-0"
              style={{ color: 'var(--text-secondary)' }}
            />
            <span>Contact us</span>
          </button>

          {/* Admin — only rendered for admins (server-resolved isAdmin). The
              /admin route is separately guarded by requireAdmin, so this is a
              discoverability affordance, not the authorization boundary. */}
          {isAdmin && (
            <Link
              href="/admin"
              onClick={() => setIsOpen(false)}
              className={ROW_CLASSES}
              style={{ color: 'var(--text-secondary)' }}
              {...POPOVER_ROW_HOVER_HANDLERS}
            >
              <Shield
                className="w-4 h-4 flex-shrink-0"
                style={{ color: 'var(--text-secondary)' }}
              />
              <span>Admin</span>
            </Link>
          )}

          {/* Sign out */}
          <button
            type="button"
            onClick={handleSignOut}
            className={ROW_CLASSES}
            style={{ color: 'var(--text-secondary)' }}
            {...POPOVER_ROW_HOVER_HANDLERS}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-secondary)' }} />
            <span>Sign out</span>
          </button>

          {/* Footer: Terms · Privacy */}
          <div
            className="flex items-center justify-center gap-2 px-3 py-2"
            style={{
              borderTop: '1px solid var(--border-default)',
              fontSize: '11px',
            }}
          >
            <Link
              href="/policies/terms"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              style={{ color: 'var(--text-tertiary)' }}
              className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
            >
              Terms
            </Link>
            <span style={{ color: 'var(--text-tertiary)' }}>·</span>
            <Link
              href="/policies/privacy"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              style={{ color: 'var(--text-tertiary)' }}
              className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
            >
              Privacy
            </Link>
          </div>
        </div>
      )}

      <ContactUsModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
